'use client';

import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import "@/styles/libs/arrow-animation.css";
import React, { FC, useState } from 'react';

export type KeyFeaturesProps = {
  keyFeatureHeaders: {
    header: string;
    planOne: string;
    planTwo: string;
    planThree: string;
  }[];
  features: {
    toolTip?: React.ReactNode;
    title: string;
    columnOne: React.ReactNode;
    columnTwo: React.ReactNode;
    columnThree: React.ReactNode;
  }[]
}

const KeyFeatures: FC<KeyFeaturesProps> = ({
  keyFeatureHeaders = [], features = []
}) => {
  const [items] = useState(features);
  const lastItem = items.length - 1;
  const secondLastItem = items.length - 2;

  const { modalActionHandler } = useModalButtonActions();
  const [toolTip, setToolTip] = useState(1000);

  return (
    <>
      <section className="flex pt-0 md:pt-[56px] bg-white md:bg-gray-100 md:pb-[80px] px-[24px] overflow-scroll md:overflow-visible">
        <div className="w-full mx-auto max-w-grid grid grid-cols-[repeat(4,minmax(150px,1fr))] md:grid-cols-4 gap-x-2 rounded-12">
          <div className="col-span-full grid w-full mx-auto max-w-grid grid-cols-4 bg-white md:bg-gray-100 gap-x-2 md:sticky top-[80px] z-[100] pt-[64px]">
            {keyFeatureHeaders.map((plan, index) => (
              <React.Fragment key={`feature-header-${index}`}>
                <div className="text-blue-900 font-500 text-caption1 md:text-h1 mt-[12px] min-w-[200px]">
                  {plan.header}
                </div>
                <div className="bg-white rounded-t-[24px] text-caption1 md:text-h4 text-blue-900 md:font-500 justify-center flex pt-[20px] pb-[16px] py-[24px] min-w-[200px]">
                  {plan.planOne}
                </div>
                <div className="bg-white rounded-t-[24px] text-caption1 md:text-h4 text-blue-900 md:font-500 justify-center flex pt-[20px] pb-[16px] py-[24px] min-w-[200px]">
                  {plan.planTwo}
                </div>
                <div className="bg-white rounded-t-[24px] text-caption1 md:text-h4 text-blue-900 md:font-500 justify-center flex pt-[20px] pb-[16px] py-[24px] md:border-t-8 md:border-x-8 md:border-solid md:border-[#b275f4]/50 md:mt-[-8px] min-w-[200px]">
                  {plan.planThree}
                </div>
              </React.Fragment>
            ))}
          </div>


          {features.map((feature, index) => (
            <React.Fragment key={`feature-${index}`}>
              <div
                className={`flex relative text-gray-700 text-caption1 py-[16px] items-center md:items-end min-w-[200px] ${lastItem === index ? "hidden md:flex" : null}`}
              >
                <p
                  className={`${feature?.toolTip ? "cursor-pointer border-b border-dotted" : null} text-14/20 tracking-tight lg:group-hover:text-citron-900`}
                  onMouseEnter={() => setToolTip(index)}
                  onMouseLeave={() => setToolTip(1000)}
                >{feature.title}</p>
                <span
                  className={`${feature?.toolTip ? "relative group hidden lg:block" : null}`}
                >

                  {feature?.toolTip && (
                    <span className={`absolute ${toolTip === index ? "flex" : "hidden"} absolute top-[-30px] left-2 py-1 px-[12px] text-white bg-gray-900 w-[200px] z-1 rounded-4 text-12/16`}>
                      {feature.toolTip}
                      <div className="absolute left-[-5px] top-[45%] bottom-0 right-0 m-auto">
                        <svg xmlns="http://www.w3.org/2000/svg" width="5" height="8" viewBox="0 0 5 8" fill="none">
                          <path d="M0.488043 4.39043C0.23784 4.19027 0.23784 3.80973 0.488043 3.60957L5 2.5828e-07L5 8L0.488043 4.39043Z" fill="#1C1E20" />
                        </svg>
                      </div>
                    </span>
                  )}
                </span>
              </div>
              <div className={`bg-white text blue-900 justify-center items-center flex px-[16px] py-[16px] text-center ${lastItem === index ? "hidden md:flex rounded-b-[16px] py-0 pb-[24px]" : null}  min-w-[200px]`}>
                {lastItem === index
                  ?
                  <div className="hidden md:flex">
                    <button
                      className="block w-full button button-size-medium button-secondary-violet"
                      onClick={() => {
                        modalActionHandler("trial");
                      }}
                    >
                      Try for Free
                    </button>
                  </div>
                  : feature.columnOne}
              </div>
              <div className={`bg-white text blue-900 justify-center items-center flex px-[16px] py-[16px] text-center ${lastItem === index ? " hidden md:flex rounded-b-[16px] py-0 pb-[24px]" : null} min-w-[200px]`}>
                {lastItem === index
                  ?
                  <div className="hidden md:flex">
                    <button
                      className="block w-full button button-size-medium button-secondary-violet"
                      onClick={() => {
                        modalActionHandler("demo");
                      }}
                    >
                      Request a Demo
                    </button>
                  </div>
                  : feature.columnTwo}
              </div>
              <div className={`bg-white text blue-900 justify-center items-center flex px-[16px] py-[16px] text-center ${lastItem === index ? " hidden md:flex rounded-b-[16px] py-0 pb-[24px] md:border-x-8 md:border-b-8 md:border-solid md:border-[#b275f4]/50" : "md:border-x-8 md:border-solid md:border-[#b275f4]/50"} min-w-[200px]`}>
                {lastItem === index
                  ?
                  <div className="hidden md:flex">
                    <button
                      className="block w-full button button-size-medium button-primary-violet"
                      onClick={() => {
                        modalActionHandler("demo");
                      }}
                    >
                      Request a Demo
                    </button>
                  </div>
                  : feature.columnThree}
              </div>
              {lastItem !== index && secondLastItem !== index && (
                <div className="h-[1px] bg-gray-200 col-span-full" />
              )}
            </React.Fragment>
          ))}
        </div>
      </section>
      <div className="flex md:hidden w-full justify-center mb-[12px]">
        <div className="flex">
          <button
            className="block w-full button button-size-medium button-primary-violet"
            onClick={() => {
              modalActionHandler("demo");
            }}
          >
            Request a Demo
          </button>
        </div>
      </div>
    </>
  );
};

export default KeyFeatures;
